import { Type } from '@sinclair/typebox';
import { ChartPricePeriodSchema } from '../chart-price.js';
import { FirstBuyerSchema } from '../first-buyer.js';
import { SupportedDex, SupportedDexSchema } from '../supported-dex.js';
import { SupportedNetworkSchema } from '../supported-network.js';
import { TokenModerationReasonSchema, TokenModerationStatusSchema, } from '../token/token-moderation.js';
import { TokenizedAgentSchema } from '../tx/base-tx.js';
import { DexBuyAccountsSchema, DexSellAccountsSchema, PumpFunBuyAccountsSchema, PumpFunSellAccountsSchema, PumpSwapAccountsSchema, RaydiumClmmAccountsSchema, RaydiumCpmmAccountsSchema, RaydiumV4AccountsSchema, } from '../tx/swap-tx.js';
import { PoolAthSchema } from './pool-ath.js';
import { PoolAuditSchema } from './pool-audit.js';
import { PoolCreatorSchema } from './pool-creator.js';
import { PoolFeesSchema } from './pool-fees.js';
import { PoolTokenSchema } from './pool-token.js';
export const PoolPriceChartEntrySchema = Type.Object({
    timestamp: Type.String(),
    price: Type.Object({
        open: Type.String(),
        high: Type.String(),
        low: Type.String(),
        close: Type.String(),
    }),
});
export const PoolPriceChartSchema = Type.Object({
    interval: ChartPricePeriodSchema,
    entries: Type.Array(PoolPriceChartEntrySchema),
});
export const DEFAULT_PERIOD_STATS = () => ({
    txs: 0,
    buys: 0,
    sells: 0,
    volumeInUsd: '0',
    buyVolumeInUsd: '0',
    sellVolumeInUsd: '0',
    netVolumeInUsd: '0',
    makers: 0,
    buyers: 0,
    sellers: 0,
    startPrice: '0',
    endPrice: '0',
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    priceChangePercents: '0',
    volumeChangePercents: '0',
    holdersChangePercents: '0',
    updatedAt: new Date().toISOString(),
});
export const PoolPeriodStatsSchema = Type.Object({
    txs: Type.Number(),
    buys: Type.Number(),
    sells: Type.Number(),
    volumeInUsd: Type.String(),
    buyVolumeInUsd: Type.String(),
    sellVolumeInUsd: Type.String(),
    netVolumeInUsd: Type.String(),
    makers: Type.Number(),
    buyers: Type.Number(),
    sellers: Type.Number(),
    startPrice: Type.String(),
    endPrice: Type.String(),
    startDate: Type.String(),
    endDate: Type.String(),
    priceChangePercents: Type.String(),
    volumeChangePercents: Type.String(),
    holdersChangePercents: Type.String(),
    updatedAt: Type.String(),
});
export const PoolAllPeriodStatsSchema = Type.Object({
    '5M': PoolPeriodStatsSchema,
    '1H': PoolPeriodStatsSchema,
    '6H': PoolPeriodStatsSchema,
    '1D': PoolPeriodStatsSchema,
    ALL: PoolPeriodStatsSchema,
});
export const PoolTimePeriodStatsSchema = Type.Object({
    '5M': PoolPeriodStatsSchema,
    '1H': PoolPeriodStatsSchema,
    '6H': PoolPeriodStatsSchema,
    '1D': PoolPeriodStatsSchema,
});
const PoolHoldersSchema = Type.Object({
    count: Type.String(),
    top10Holding: Type.String(),
});
export const PoolWalletGroupSchema = Type.Object({
    count: Type.String(),
    holding: Type.String(),
});
export const PoolToMigrationSchema = Type.Object({
    migratedToPoolAddress: Type.String(),
    migratedToProvider: SupportedDexSchema,
    migrationTimestamp: Type.String(),
    migrationPrice: Type.String(),
    migrationMarketCap: Type.String(),
    migrationTxHash: Type.String(),
});
export const PoolFromMigrationSchema = Type.Object({
    migratedFromPoolAddress: Type.String(),
    migratedFromProvider: SupportedDexSchema,
    migrationTimestamp: Type.String(),
    migrationPrice: Type.String(),
    migrationMarketCap: Type.String(),
    migrationTxHash: Type.String(),
});
export const PumpFunQuoteCurrencySchema = Type.Union([
    Type.Literal('sol'),
    Type.Literal('usdc'),
]);
export const PoolBondingCurveSchema = Type.Object({
    curveType: Type.String(),
    bondingPercentage: Type.String(),
    graduationPrice: Type.String(),
    graduationMcap: Type.String(),
    graduationQuoteCurrency: Type.Optional(PumpFunQuoteCurrencySchema),
});
export const PoolExtraDetailsSchema = Type.Union([
    Type.Object({
        provider: Type.Literal(SupportedDex.PumpFun),
        migration: Type.Optional(PoolToMigrationSchema),
        isMayhemMode: Type.Optional(Type.Boolean()),
        isCashbackMode: Type.Optional(Type.Boolean()),
        buybackFeeBasisPoints: Type.Optional(Type.String()),
        tokenizedAgent: Type.Optional(TokenizedAgentSchema),
        bondingCurve: Type.Optional(PoolBondingCurveSchema),
    }),
    Type.Object({
        provider: Type.Literal(SupportedDex.PumpSwap),
        migration: Type.Optional(PoolFromMigrationSchema),
        isMayhemMode: Type.Optional(Type.Boolean()),
        isCashbackMode: Type.Optional(Type.Boolean()),
        cashback: Type.Optional(Type.String()),
        cashbackFeeBasisPoints: Type.Optional(Type.String()),
        coinCreator: Type.Optional(Type.String()),
        coinCreatorFee: Type.Optional(Type.String()),
        coinCreatorFeeBasisPoints: Type.Optional(Type.String()),
        buybackFee: Type.Optional(Type.String()),
        buybackFeeBasisPoints: Type.Optional(Type.String()),
    }),
    Type.Object({
        provider: Type.Literal(SupportedDex.RaydiumCPMM),
        migration: Type.Optional(PoolFromMigrationSchema),
    }),
    Type.Object({
        provider: Type.Literal(SupportedDex.RaydiumV4),
        migration: Type.Optional(PoolFromMigrationSchema),
    }),
    Type.Object({
        provider: Type.Literal(SupportedDex.RaydiumLaunchpad),
        migration: Type.Optional(PoolToMigrationSchema),
        bondingCurve: Type.Optional(PoolBondingCurveSchema),
    }),
    Type.Object({
        provider: Type.Literal(SupportedDex.BonkFun),
        migration: Type.Optional(PoolToMigrationSchema),
        bondingCurve: Type.Optional(PoolBondingCurveSchema),
    }),
]);
const PoolVariant = (provider, accounts) => Type.Object({
    provider: Type.Union([Type.Literal(provider), Type.Literal(provider)]),
    accounts: Type.Optional(accounts),
});
export const PoolAccountsSchema = Type.Union([
    PoolVariant(SupportedDex.RaydiumV4, Type.Object({
        buy: RaydiumV4AccountsSchema,
        sell: RaydiumV4AccountsSchema,
    })),
    PoolVariant(SupportedDex.RaydiumCPMM, Type.Object({
        buy: RaydiumCpmmAccountsSchema,
        sell: RaydiumCpmmAccountsSchema,
    })),
    PoolVariant(SupportedDex.RaydiumCLMM, Type.Object({
        buy: RaydiumClmmAccountsSchema,
        sell: RaydiumClmmAccountsSchema,
    })),
    PoolVariant(SupportedDex.PumpSwap, Type.Object({
        buy: PumpSwapAccountsSchema,
        sell: PumpSwapAccountsSchema,
    })),
    PoolVariant(SupportedDex.PumpFun, Type.Object({
        buy: PumpFunBuyAccountsSchema,
        sell: PumpFunSellAccountsSchema,
    })),
    Type.Object({
        provider: SupportedDexSchema,
        accounts: Type.Undefined(),
    }),
]);
export const PoolModelAccountsSchema = Type.Object({
    buy: Type.Optional(DexBuyAccountsSchema),
    sell: Type.Optional(DexSellAccountsSchema),
});
export const CreatorStatsSchema = Type.Object({
    totalPools: Type.Number(),
    successfulPools: Type.Number(),
    ruggedPools: Type.Number(),
    isDangerous: Type.Boolean(),
    bondedPools: Type.Optional(Type.Number()),
    bondedPercent: Type.Optional(Type.Number()),
    created7d: Type.Optional(Type.Number()),
    solBalance: Type.Optional(Type.String()),
    topTokens: Type.Optional(Type.Array(Type.Object({
        address: Type.String(),
        pairAddress: Type.Optional(Type.String()),
        name: Type.String(),
        symbol: Type.String(),
        logo: Type.String(),
        status: Type.Optional(TokenModerationStatusSchema),
        moderationReason: Type.Optional(TokenModerationReasonSchema),
        currentMarketCap: Type.String(),
        athMarketCap: Type.String(),
        traders24h: Type.Number(),
        initTimestamp: Type.String(),
        migrated: Type.Optional(Type.Boolean()),
    }))),
    qualifiedPools: Type.Optional(Type.Number()),
    previousProjects: Type.Optional(Type.Array(Type.Object({
        address: Type.String(),
        name: Type.String(),
        symbol: Type.String(),
        logo: Type.String(),
        status: Type.Optional(TokenModerationStatusSchema),
        moderationReason: Type.Optional(TokenModerationReasonSchema),
        currentMarketCap: Type.String(),
        athMarketCap: Type.String(),
        migrated: Type.Boolean(),
        fullLiquidityFromStart: Type.Boolean(),
        initTimestamp: Type.String(),
    }))),
    lastTokenLaunchedAt: Type.Optional(Type.String()),
    updatedAt: Type.String(),
});
export const PoolSchema = Type.Object({
    address: Type.String(),
    network: SupportedNetworkSchema,
    provider: SupportedDexSchema,
    pair: Type.Tuple([Type.String(), Type.String()]),
    baseToken: PoolTokenSchema,
    quoteToken: PoolTokenSchema,
    marketCap: Type.String(),
    liquidityInNative: Type.String(),
    liquidityInToken: Type.String(),
    liquidityInUsd: Type.String(),
    fees: PoolFeesSchema,
    tokenPriceInNative: Type.String(),
    tokenPriceInUsd: Type.String(),
    periodStats: PoolAllPeriodStatsSchema,
    holders: PoolHoldersSchema,
    insiders: PoolWalletGroupSchema,
    snipers: PoolWalletGroupSchema,
    bundlers: PoolWalletGroupSchema,
    firstBuyers: Type.Array(FirstBuyerSchema),
    /**
     * How many bought first and how many of those sniped.
     *
     * A list row shows the two numbers and nothing else, and the wallets
     * themselves are six kilobytes of the thirteen a card weighs — half of
     * what an explore page sends, for a badge that reads "3/70". The feeds
     * send this and leave the array empty; a card read on its own carries
     * both.
     */
    firstBuyersMeta: Type.Optional(Type.Object({ total: Type.Number(), snipers: Type.Number() })),
    audit: PoolAuditSchema,
    extraDetails: Type.Optional(PoolExtraDetailsSchema),
    accounts: Type.Optional(PoolModelAccountsSchema),
    initTxSignature: Type.Optional(Type.String()),
    initTimestamp: Type.Optional(Type.String()),
    initBlock: Type.Optional(Type.Number()),
    initTxCreator: Type.Optional(PoolCreatorSchema),
    allTimeHigh: PoolAthSchema,
    creatorStats: Type.Optional(CreatorStatsSchema),
    updatedAt: Type.String(),
    infoUpdatedAt: Type.Optional(Type.String()),
    metricsUpdatedAt: Type.Optional(Type.String()),
    chartsUpdatedAt: Type.Optional(Type.String()),
    lpToken: Type.Optional(Type.String()),
    migrated: Type.Optional(Type.Boolean()),
    deleted: Type.Optional(Type.Boolean()),
});
export const ResponsePoolSchema = Type.Object({
    ...PoolSchema.properties,
    priceChart: PoolPriceChartSchema,
});
export const PoolLightSchema = Type.Object({
    address: Type.String(),
    base: PoolTokenSchema,
    quote: PoolTokenSchema,
    provider: SupportedDexSchema,
    pair: Type.Tuple([Type.String(), Type.String()]),
});
//# sourceMappingURL=pool.js.map